import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePlayer } from '../contexts/PlayerContext';
import { useSocket } from '../contexts/SocketContext';
import { logAnswerSubmitted } from '../lib/firebase';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Users, Clock, Trophy, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

function GameRoom() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  
  const { player, isAuthenticated } = usePlayer();
  const { 
    currentQuestion, 
    players, 
    gameStarted, 
    gameEnded, 
    connected,
    submitAnswer,
    joinRoom
  } = useSocket();

  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [gameState, setGameState] = useState<'open' | 'active' | 'closed' | null>(null);
  const [gameTitle, setGameTitle] = useState<string>('Tech Knowledge Challenge');
  const [gameMode, setGameMode] = useState<'synchronized' | 'self-paced'>('synchronized');
  const [playerCompleted, setPlayerCompleted] = useState(false);
  const [isCheckingGameState, setIsCheckingGameState] = useState(true);
  const [loadingNextQuestion, setLoadingNextQuestion] = useState(false);
  const [stuckQuestion, setStuckQuestion] = useState(false);
  const selectedAnswerRef = useRef<number | null>(null);
  const hasSubmittedRef = useRef<boolean>(false);
  const playerRef = useRef(player);
  const submitAnswerRef = useRef(submitAnswer);
  const joinRoomRef = useRef(joinRoom);
  const hasJoinedRef = useRef<boolean>(false);
  const currentQuestionIdRef = useRef<string | null>(null);
  const stuckQuestionTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Keep refs in sync with state and props
  useEffect(() => {
    selectedAnswerRef.current = selectedAnswer;
  }, [selectedAnswer]);

  useEffect(() => {
    hasSubmittedRef.current = hasSubmitted;
  }, [hasSubmitted]);

  useEffect(() => {
    playerRef.current = player;
  }, [player]);

  useEffect(() => {
    submitAnswerRef.current = submitAnswer;
  }, [submitAnswer]);

  useEffect(() => {
    joinRoomRef.current = joinRoom;
  }, [joinRoom]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Check game state when component mounts
  useEffect(() => {
    const checkGameState = async () => {
      if (!roomId) return;
      
      try {
        setIsCheckingGameState(true);
        const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
        const response = await fetch(`${serverUrl}/api/question-sets/${roomId}`);
        
        if (response.ok) {
          const data = await response.json();
          const questionSet = data.questionSet;
          setGameState(questionSet.state);
          setGameTitle(questionSet.title || 'Tech Knowledge Challenge');
          setGameMode(questionSet.gameMode || 'synchronized');
          console.log(`🎮 Game mode: ${questionSet.gameMode || 'synchronized'}`);
          
          // If game is closed, redirect to leaderboard
          if (questionSet.state === 'closed') {
            console.log('🔒 Game is closed, redirecting to leaderboard');
            navigate(`/leaderboard/${roomId}`);
            return;
          }
        } else {
          console.warn('⚠️ Could not fetch game state, assuming game is active');
          setGameState('active');
        }
      } catch (error) {
        console.error('❌ Error checking game state:', error);
        setGameState('active'); // Default to active if we can't check
      } finally {
        setIsCheckingGameState(false);
      }
    };

    checkGameState();
  }, [roomId, navigate]);

  // Join room when component mounts
  useEffect(() => {
    if (roomId && player && connected && isAuthenticated && !hasJoinedRef.current) {
      console.log(`🎮 Joining room ${roomId} as ${player.nickname}`);
      joinRoomRef.current(roomId, player.nickname, player.id);
      hasJoinedRef.current = true;
    }
  }, [roomId, player, connected, isAuthenticated]);

  // Reset join status when room changes
  useEffect(() => {
    hasJoinedRef.current = false;
  }, [roomId]);

  // Retry joining room if disconnected and reconnected
  useEffect(() => {
    if (roomId && player && connected && isAuthenticated && players.length === 0 && hasJoinedRef.current) {
      console.log(`🔄 Retrying room join for ${roomId}`);
      const retryTimeout = setTimeout(() => {
        joinRoomRef.current(roomId, player.nickname, player.id);
      }, 1000);
      return () => clearTimeout(retryTimeout);
    }
  }, [connected, roomId, player, isAuthenticated, players.length]);

  // Redirect if game ended
  useEffect(() => {
    if (gameEnded && roomId) {
      navigate(`/leaderboard/${roomId}`);
    }
  }, [gameEnded, roomId, navigate]);

  // Timer countdown
  useEffect(() => {
    if (!gameStarted || !currentQuestion) {
      return;
    }

    console.log('⏰ Starting timer for question:', currentQuestion.id);
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // Auto-submit when time runs out
          if (!hasSubmittedRef.current && playerRef.current && roomId) {
            submitAnswerRef.current(roomId, playerRef.current.id, selectedAnswerRef.current || -1, 0);
            setHasSubmitted(true);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      console.log('⏰ Timer finished for question:', currentQuestion.id);
      clearInterval(timer);
    };
  }, [gameStarted, currentQuestion, roomId]);

  // Reset state for new question (only when question ID actually changes)
  useEffect(() => {
    if (currentQuestion && currentQuestion.id !== currentQuestionIdRef.current) {
      console.log(`🔄 [${new Date().toISOString()}] New question detected:`, currentQuestion.id);
      currentQuestionIdRef.current = currentQuestion.id;
      setSelectedAnswer(null);
      setTimeRemaining(30);
      setHasSubmitted(false);
      setLoadingNextQuestion(false);
      setStuckQuestion(false);
      
      // Clear any stuck question timer
      if (stuckQuestionTimerRef.current) {
        clearTimeout(stuckQuestionTimerRef.current);
        stuckQuestionTimerRef.current = null;
      }
    }
  }, [currentQuestion]);
  
  // Detect stuck questions - if we've submitted but no new question after 40 seconds
  useEffect(() => {
    if (hasSubmitted && gameMode === 'synchronized' && !gameEnded) {
      console.log(`⏰ [${new Date().toISOString()}] Starting stuck question detection timer (40s)`);
      
      // Clear any existing timer
      if (stuckQuestionTimerRef.current) {
        clearTimeout(stuckQuestionTimerRef.current);
      }
      
      // Start 40-second timer to detect stuck questions
      stuckQuestionTimerRef.current = setTimeout(() => {
        console.error(`⚠️ [${new Date().toISOString()}] STUCK QUESTION DETECTED - No new question received after 40 seconds`);
        setStuckQuestion(true);
      }, 40000);
      
      return () => {
        if (stuckQuestionTimerRef.current) {
          clearTimeout(stuckQuestionTimerRef.current);
          stuckQuestionTimerRef.current = null;
        }
      };
    }
  }, [hasSubmitted, gameMode, gameEnded]);
  
  // Listen for player_completed event in self-paced mode
  useEffect(() => {
    if (!roomId || gameMode !== 'self-paced') return;
    
    // Import socket.io-client to listen for events
    import('socket.io-client').then(({ io }) => {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      const socket = io(`${serverUrl}/game`, {
        path: '/ws/',
        transports: ['websocket', 'polling'],
      });
      
      socket.on('player_completed', () => {
        console.log('🏁 Player completed all questions in self-paced mode');
        setPlayerCompleted(true);
      });
      
      return () => {
        socket.off('player_completed');
        socket.disconnect();
      };
    });
  }, [roomId, gameMode]);

  const handleAnswerSelect = (answerIndex: number) => {
    if (hasSubmitted || !gameStarted) return;
    setSelectedAnswer(answerIndex);
  };

  const handleSubmitAnswer = () => {
    if (!player || !roomId || hasSubmitted || selectedAnswer === null || !currentQuestion) return;
    
    // Track answer submission (we don't know if it's correct yet, so we'll track it as submitted)
    logAnswerSubmitted(currentQuestion.id, false, timeRemaining);
    
    submitAnswer(roomId, player.id, selectedAnswer, timeRemaining * 1000);
    setHasSubmitted(true);
    
    // Set loading state for self-paced mode
    if (gameMode === 'self-paced') {
      setLoadingNextQuestion(true);
    }
  };

  // Show loading state while checking game state
  if (isCheckingGameState) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/lobby')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-sans font-semibold text-lg">{gameTitle}</h1>
          </div>

          <Card className="text-center">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              </div>
              <CardTitle className="font-sans font-semibold text-xl">Loading Game...</CardTitle>
              <CardDescription className="font-mono">
                Checking game status
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  // Show closed game message
  if (gameState === 'closed') {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/lobby')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-sans font-semibold text-lg">{gameTitle}</h1>
          </div>

          <Card className="text-center">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 bg-destructive/10 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                </div>
              </div>
              <CardTitle className="font-sans font-semibold text-xl">Game Closed</CardTitle>
              <CardDescription className="font-mono">
                This game has ended and is no longer accepting players
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  The game you're trying to join has been closed by the administrator.
                </p>
                <Button onClick={() => navigate(`/leaderboard/${roomId}`)} className="w-full">
                  View Final Results
                </Button>
                <Button variant="outline" onClick={() => navigate('/lobby')} className="w-full">
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Waiting for players screen
  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/lobby')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-sans font-semibold text-lg">{gameTitle}</h1>
          </div>

          <Card className="text-center">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <Trophy className="h-8 w-8 text-primary" />
                </div>
              </div>
              <CardTitle className="font-sans font-semibold text-xl">Get Ready!</CardTitle>
              <CardDescription className="font-mono">
                Waiting for the game to start
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex flex-col items-center gap-3">
                  <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{players.length} players</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>30s per question</span>
                    </div>
                  </div>
                  <Badge variant={gameMode === 'synchronized' ? 'secondary' : 'outline'} className="text-xs">
                    {gameMode === 'synchronized' ? '🔄 Synchronized Mode' : '🏃 Self-Paced Mode'}
                  </Badge>
                  <p className="text-xs text-center text-muted-foreground max-w-sm">
                    {gameMode === 'synchronized' 
                      ? 'All players will answer each question together before moving to the next one' 
                      : 'You can progress through questions at your own pace'}
                  </p>
                </div>
                
                {/* Connection Status */}
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  connected 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-destructive/10 text-destructive'
                }`}>
                  <div className={`w-2 h-2 rounded-full mr-2 ${
                    connected ? 'bg-green-500' : 'bg-destructive'
                  }`}></div>
                  {connected ? 'Connected' : 'Connecting...'}
                </div>

                {/* Players List */}
                <div className="space-y-2">
                  {players.map((p) => (
                    <div 
                      key={p.id} 
                      className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                        p.id === player?.id ? 'bg-primary/10 text-primary' : 'bg-muted/30'
                      }`}
                    >
                      <span className="font-mono text-sm">{p.nickname}</span>
                      <div className="flex items-center gap-2">
                        {p.id === player?.id && <span className="text-xs font-mono">(You)</span>}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Waiting Message */}
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Get ready to play!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Game screen
  return (
    <div className="min-h-screen bg-background p-3">
      <div className="max-w-md mx-auto">
        {/* Compact Header with Timer */}
        <div className="relative flex items-center justify-center mb-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/lobby')}
            className="absolute left-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary/10">
            <Clock className="h-4 w-4 text-primary" />
            <span
              className={`text-lg font-sans font-semibold ${
                timeRemaining <= 10 ? "text-destructive animate-pulse" : "text-primary"
              }`}
            >
              {timeRemaining}s
            </span>
          </div>
        </div>

        {/* Question */}
        {currentQuestion && (
          <Card className="mb-3">
            <CardHeader className="pb-3">
              <CardTitle className="font-sans font-semibold text-base leading-tight">
                {currentQuestion.body}
              </CardTitle>
            </CardHeader>
          </Card>
        )}

        {/* Answer Options */}
        <div className="space-y-2 mb-3">
          {currentQuestion?.choices.map((choice, index) => {
            let buttonVariant: "outline" | "default" | "destructive" = "outline";
            let icon = null;
            let buttonClassName = "w-full h-auto p-3 text-left justify-start relative";

            // Keep selection highlighted both before and after submission
            if (selectedAnswer === index) {
              buttonVariant = "default";
              buttonClassName += " bg-primary text-primary-foreground";
              if (hasSubmitted) {
                buttonClassName += " opacity-90";
                icon = <CheckCircle className="h-5 w-5 text-green-500" />;
              }
            }

            return (
              <Button
                key={index}
                variant={buttonVariant}
                className={buttonClassName}
                onClick={() => handleAnswerSelect(index)}
                disabled={hasSubmitted || timeRemaining === 0}
              >
                <div className="flex items-center justify-between w-full">
                  <span className="font-mono text-sm">{choice}</span>
                  {icon}
                </div>
              </Button>
            );
          })}
        </div>

        {/* Submit Button */}
        {!hasSubmitted && timeRemaining > 0 && selectedAnswer !== null && (
          <Button
            onClick={handleSubmitAnswer}
            size="lg"
            className="w-full mb-3"
          >
            Submit Answer ✓
          </Button>
        )}

        {/* Player Completed All Questions (Self-Paced Mode) */}
        {playerCompleted && gameMode === 'self-paced' && (
          <Card className="mb-3 bg-blue-50 border-blue-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 text-blue-800">
                <Trophy className="h-4 w-4" />
                <span className="font-mono text-xs font-medium">You've completed all questions! Check the leaderboard to see your rank.</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stuck Question Detection Error */}
        {stuckQuestion && (
          <Card className="mb-3 border-2 border-red-500 bg-red-50">
            <CardHeader className="pb-2">
              <div className="flex justify-center mb-2">
                <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
              </div>
              <CardTitle className="font-sans font-semibold text-base text-red-900 text-center">
                Game Seems Stuck
              </CardTitle>
              <CardDescription className="text-center text-red-700 text-xs">
                The game hasn't advanced to the next question. This might be a temporary issue.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="space-y-2">
                <p className="text-xs text-red-800 text-center">
                  You can try refreshing the page or returning to the lobby.
                </p>
                <div className="flex flex-col gap-2">
                  <Button 
                    onClick={() => window.location.reload()} 
                    variant="default"
                    className="w-full bg-red-600 hover:bg-red-700"
                  >
                    Refresh Page
                  </Button>
                  <Button 
                    onClick={() => navigate('/lobby')} 
                    variant="outline"
                    className="w-full"
                  >
                    Return to Lobby
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Status Messages - More Prominent */}
        {hasSubmitted && !playerCompleted && (
          <Card className="mb-3 border-2 border-green-500 bg-green-50">
            <CardContent className="p-4">
              <div className="flex flex-col items-center gap-2">
                <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div className="text-center">
                  <p className="font-sans font-semibold text-base text-green-900 mb-0.5">
                    Answer Submitted!
                  </p>
                  <p className="font-mono text-xs text-green-700">
                    {gameMode === 'synchronized' 
                      ? 'Waiting for other players to finish...' 
                      : 'Loading your next question...'}
                  </p>
                </div>
                {/* Loading spinner for self-paced mode */}
                {loadingNextQuestion && gameMode === 'self-paced' && (
                  <div className="flex justify-center mt-1">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {!hasSubmitted && timeRemaining === 0 && (
          <Card className="mb-3 bg-red-50 border-red-200">
            <CardContent className="p-3">
              <div className="flex items-center gap-2 text-red-800">
                <XCircle className="h-4 w-4" />
                <span className="font-mono text-xs font-medium">Time's up! Moving to next question...</span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export default GameRoom; 